import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Mic, Pencil, Trash2 } from "lucide-react";
import AppLayout from "../../shared/components/AppLayout";
import Button from "../../shared/components/Button";
import patientService from "../../services/patientService";
import { DoctorPatient, GENDER_OPTIONS } from "../../types/patient";

const GENDER_LABEL: Record<string, string> = Object.fromEntries(
  GENDER_OPTIONS.map((o) => [o.value, o.label]),
);

const DetailField: React.FC<{
  label: string;
  value: string | null | undefined;
}> = ({ label, value }) => (
  <div className="py-4 border-b border-gray-100 last:border-0">
    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
      {label}
    </dt>
    <dd className="text-sm text-gray-900">
      {value || <span className="text-gray-400">—</span>}
    </dd>
  </div>
);

const PatientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [patient, setPatient] = useState<DoctorPatient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this patient? This action cannot be undone.",
      )
    ) {
      try {
        await patientService.deleteDoctorPatient(Number(id));
        navigate("/patients");
      } catch (err: any) {
        setError("Failed to delete patient. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const data = await patientService.fetchDoctorPatient(Number(id));
        setPatient(data);
      } catch (err: any) {
        setError(
          err.response?.data?.detail || err.message || "Failed to load patient",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const getInitials = (p: DoctorPatient) =>
    `${p.first_name.charAt(0)}${p.last_name.charAt(0)}`.toUpperCase() ||
    p.name.charAt(0).toUpperCase();

  const displayEmail =
    patient?.email && !patient.email.includes("@placeholder.docfi")
      ? patient.email
      : null;

  return (
    <AppLayout active="patients">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          size="sm"
          icon={ArrowLeft}
          onClick={() => navigate("/patients")}
          className="mb-6 -ml-2"
        >
          Back to Patients
        </Button>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
            {error}
          </div>
        ) : patient ? (
          <>
            {/* Patient header card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 flex items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-3xl font-bold text-blue-700">
                  {getInitials(patient)}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {patient.first_name} {patient.last_name}
                  </h1>
                  {displayEmail && (
                    <p className="text-gray-500 text-sm mt-1">{displayEmail}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {GENDER_LABEL[patient.gender] ||
                        patient.gender ||
                        "Unknown"}
                    </span>
                    <span className="text-xs text-gray-400">
                      Patient #{patient.patient_id}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="success"
                  icon={Mic}
                  onClick={() =>
                    navigate(`/patients/${patient.patient_id}/record`)
                  }
                >
                  Start Encounter
                </Button>
                <Button
                  variant="secondary"
                  icon={Pencil}
                  onClick={() =>
                    navigate(`/patients/${patient.patient_id}/edit`)
                  }
                >
                  Edit
                </Button>
                <Button
                  variant="danger"
                  icon={Trash2}
                  onClick={handleDelete}
                  aria-label="Delete patient"
                >
                  Delete
                </Button>
              </div>
            </div>

            {/* Details card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-800 mb-2">
                Patient Information
              </h2>
              <dl>
                <DetailField label="First Name" value={patient.first_name} />
                <DetailField label="Last Name" value={patient.last_name} />
                <DetailField
                  label="Gender"
                  value={GENDER_LABEL[patient.gender] || patient.gender}
                />
                <DetailField
                  label="Date of Birth"
                  value={
                    patient.dob
                      ? new Date(patient.dob + "T00:00:00").toLocaleDateString(
                          undefined,
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          },
                        )
                      : null
                  }
                />
                <DetailField label="Phone" value={patient.phone} />
                <DetailField label="Email" value={displayEmail} />
                <DetailField label="Address" value={patient.address} />
                <DetailField
                  label="Created"
                  value={new Date(patient.created_at).toLocaleString()}
                />
              </dl>
            </div>
          </>
        ) : null}
      </div>
    </AppLayout>
  );
};

export default PatientDetailPage;
